import * as React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CreditCard } from 'lucide-react'

import { getBillingStatus, createPortalSession, ApiError } from '@/lib/api.js'
import { ERR, humanMessage } from '@/lib/errors.js'
import { Alert, ScreenMessage } from '@/components/ui/feedback.jsx'
import { Button } from '@/components/ui/button.jsx'

/**
 * The Manage Billing surface (Phase 0b item 9).
 *
 * Deliberately small. The plan's decision was hosted-only: Stripe owns every
 * pixel that touches a card. What MBOS owns is exactly three things — a way in
 * (the button), a warning before access is lost (the banner), and an
 * explanation after it is (the blocked screen).
 *
 * ── WHY THIS IS NOT "JUST A LINK" ───────────────────────────────────────────
 * A portal URL is a short-lived session created server-side per click, so it
 * cannot be an <a href> written at render time. Each press mints a fresh one.
 */

/**
 * Send the browser to a freshly-minted Stripe portal session.
 *
 * A full navigation, not an iframe: Stripe sets frame-ancestors, so an embedded
 * attempt renders an empty box where the card form should be — which looks
 * exactly like a broken product at the worst possible moment.
 */
export function useBillingPortal () {
  return useMutation({
    mutationFn: createPortalSession,
    onSuccess: ({ url }) => { window.location.assign(url) }
  })
}

/** Shared button so the banner, the settings card and the blocked screen agree. */
export function ManageBillingButton ({ variant = 'primary', className, children }) {
  const portal = useBillingPortal()

  return (
    <div className={className}>
      <Button
        variant={variant}
        onClick={() => portal.mutate()}
        loading={portal.isPending}
      >
        {children ?? 'Manage billing'}
      </Button>
      {portal.error && (
        <p className="mt-2 text-sm text-danger-ink">{humanMessage(portal.error)}</p>
      )}
    </div>
  )
}

/**
 * The `past_due` warning.
 *
 * Renders nothing unless there is something to say — a banner that is always
 * present is a banner nobody reads.
 *
 * Tone is `warn`, not `danger`, and the copy does not accuse: at this point
 * Stripe is still retrying and most of these resolve on their own. The job here
 * is to make the one case that will NOT resolve visible early enough to fix.
 */
export function BillingBanner ({ status }) {
  if (status !== 'past_due') return null

  return (
    <Alert
      tone="warn"
      title="We could not collect your last payment"
      className="mb-5"
      action={<ManageBillingButton variant="secondary">Update payment method</ManageBillingButton>}
    >
      <p>
        Your card was declined and we are retrying automatically. Everything still
        works — but if the retries do not succeed, this workspace will be paused.
      </p>
    </Alert>
  )
}

/**
 * The full-screen state for a workspace that has actually lost access.
 *
 * A ScreenMessage rather than a banner, for the same reason `no_tenant_claim`
 * is: there is no usable dashboard behind it, and rendering an empty one would
 * leave a contractor unable to tell "your subscription lapsed" from "your data
 * is gone". Those need very different words, and only one of them is true.
 *
 * `canceled` gets different copy from `suspended` on purpose — the reassurance
 * that the data still exists is the single most important sentence on this
 * screen, and it is the thing a departing customer least expects to read.
 */
export function SubscriptionBlocked ({ slug, canOpenPortal = true, onSignOut }) {
  const canceled = slug === ERR.SUBSCRIPTION_CANCELED

  return (
    <ScreenMessage
      tone="warn"
      title={canceled ? 'This subscription has ended' : 'This workspace is paused'}
      action={
        <div className="flex flex-col gap-2">
          {canOpenPortal && (
            <ManageBillingButton>
              {canceled ? 'Restart subscription' : 'Update payment method'}
            </ManageBillingButton>
          )}
          {onSignOut && (
            <Button variant="ghost" onClick={onSignOut}>Sign out</Button>
          )}
        </div>
      }
    >
      <p>{humanMessage({ slug })}</p>
      <p>
        <strong className="font-medium text-ink">Nothing has been deleted.</strong>{' '}
        Your clients, jobs, estimates and invoices are all still here and will be
        exactly as you left them.
      </p>
      {!canOpenPortal && (
        <p>
          There is no billing account on this workspace yet, so there is nothing to
          update from here — get in touch and we will sort it out.
        </p>
      )}
    </ScreenMessage>
  )
}

/**
 * The settings card.
 *
 * Shows the plan and its state, and gives the one button. Loading and failure
 * are handled quietly: billing is not the reason someone opened Settings, so a
 * spinner or an error here must never look like the page itself broke.
 */
export function BillingCard () {
  const { data, error, isPending } = useQuery({
    queryKey: ['billing-status'],
    queryFn: getBillingStatus,
    staleTime: 60 * 1000,
    retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2
  })

  const billing = data?.billing

  return (
    <section className="rounded-lg border border-line bg-surface p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-subtle">
          <CreditCard className="size-4 text-brand" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-ink">Billing</h2>
          <p className="mt-0.5 text-sm text-ink-muted">
            Your MBOS subscription, payment method and invoices.
          </p>

          {isPending && <p className="mt-4 text-sm text-ink-subtle">Loading…</p>}

          {error && (
            <p className="mt-4 text-sm text-ink-muted">{humanMessage(error)}</p>
          )}

          {billing && (
            <>
              <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                <div className="flex justify-between gap-4 sm:block">
                  <dt className="text-ink-subtle">Plan</dt>
                  <dd className="font-medium capitalize text-ink sm:mt-0.5">{billing.tier}</dd>
                </div>
                <div className="flex justify-between gap-4 sm:block">
                  <dt className="text-ink-subtle">Status</dt>
                  <dd className="sm:mt-0.5"><StatusLabel status={billing.status} /></dd>
                </div>
              </dl>

              {billing.can_open_portal
                ? (
                  <ManageBillingButton variant="secondary" className="mt-5">
                    Manage billing
                  </ManageBillingButton>
                  )
                : (
                  <p className="mt-5 text-sm text-ink-muted">
                    No billing account is set up on this workspace yet.
                  </p>
                  )}

              <p className="mt-3 text-xs text-ink-subtle">
                Payment details, invoices and receipts are handled by Stripe. MBOS
                never stores your card.
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  )
}

/** Plain-language status. `trialing`/`past_due` mean nothing to a contractor. */
function StatusLabel ({ status }) {
  const COPY = {
    trial: ['Free trial', 'text-info-ink'],
    active: ['Active', 'text-success-ink'],
    past_due: ['Payment failed — retrying', 'text-warn-ink'],
    suspended: ['Paused — payment needed', 'text-danger-ink'],
    canceled: ['Ended', 'text-ink-muted']
  }
  const [label, tone] = COPY[status] ?? [status, 'text-ink-muted']
  return <span className={`font-medium ${tone}`}>{label}</span>
}
